/** Вкладка «Аналитика» — порт renderGraphicAnalytics (build-orders-dashboard-html.js,
 * стр. 4669-4786). Карточки, завязанные на справочник артикулов (группы-«пузыри»,
 * маркетплейсы внутри групп, план-факт по группам, характеристики) — отложены вместе
 * со справочником. Добавлены денежные карточки себестоимость/маржа (решение v1). */
import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { OrdersMatrixReport } from "@/api/orders";
import * as planningApi from "@/api/orders-planning";
import { axisTick } from "@/lib/chart-theme";
import { useIsDark } from "@/lib/use-is-dark";
import { cn } from "@/lib/utils";
import { MpDonut } from "./Charts";
import { compact, int0, monthFullLabel, monthLabel, signed } from "./format";
import {
  buildView,
  marginPct,
  rowMetrics,
  type RowMetrics,
  type ViewContext,
  type ViewRow,
} from "./metrics";

function Card({
  title,
  sub,
  children,
  className,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn("rounded-md border bg-card p-3", className)}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="text-sm font-medium">{title}</div>
        {sub && <div className="text-right text-[11px] text-muted-foreground">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

/** Цвета помесячных баров (recharts — строками, классом `dark` не переключаются): обычный месяц и
 *  выделенный текущий, по теме. Светлая — прежние; тёмная — приглушённая база и яркий акцент. */
const MONTH_BAR_COLORS = {
  qty: { light: { base: "#cbd5e1", accent: "#0284c7" }, dark: { base: "#334155", accent: "#38bdf8" } },
  revenue: { light: { base: "#bae6fd", accent: "#0369a1" }, dark: { base: "#0c4a6e", accent: "#0ea5e9" } },
  margin: { light: { base: "#bbf7d0", accent: "#059669" }, dark: { base: "#14532d", accent: "#34d399" } },
} as const;

/** Помесячный бар (порт largeBarViz) — один тон, текущий месяц выделен. */
function MonthBar({
  months,
  values,
  tone,
  unit,
}: {
  months: string[];
  values: number[];
  tone: keyof typeof MONTH_BAR_COLORS;
  unit: string;
}): React.ReactElement {
  const isDark = useIsDark();
  const { base: color, accent } = MONTH_BAR_COLORS[tone][isDark ? "dark" : "light"];
  const data = months.map((month, i) => ({
    month,
    label: monthLabel(month),
    value: Math.round(values[i] ?? 0),
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} strokeOpacity={0.25} />
        <XAxis dataKey="label" tick={axisTick(isDark)} tickLine={false} axisLine={false} interval="preserveStartEnd" />
        <YAxis tick={axisTick(isDark)} tickLine={false} axisLine={false} width={58} tickFormatter={(v: number) => compact(v)} />
        <Tooltip
          cursor={{ fillOpacity: 0.06 }}
          content={({ active, payload }) => {
            const p = active && payload?.[0]?.payload;
            if (!p) return null;
            return (
              <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md">
                <div className="font-medium">{monthFullLabel(p.month)}</div>
                <div className="text-base font-semibold tabular-nums">{int0(p.value)} {unit}</div>
              </div>
            );
          }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={34}>
          {data.map((d, i) => (
            <Cell key={d.month} fill={i === data.length - 1 ? accent : color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

type VizTone = "sky" | "amber" | "green" | "red";

/** Тон строки списка — значение и полоса; классы парой со своим `dark:` (светлая — прежние *-600). */
const VIZ_TONE: Record<VizTone, { text: string; bar: string }> = {
  sky: { text: "text-sky-600 dark:text-sky-400", bar: "bg-sky-600 dark:bg-sky-500" },
  amber: { text: "text-amber-600 dark:text-amber-400", bar: "bg-amber-600 dark:bg-amber-500" },
  green: { text: "text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-600 dark:bg-emerald-500" },
  red: { text: "text-red-600 dark:text-red-400", bar: "bg-red-600 dark:bg-red-500" },
};

/** Порт listViz: строки с треком. */
function VizList({
  items,
}: {
  items: { name: string; value: number; suffix?: string; tone: VizTone; signed?: boolean }[];
}): React.ReactElement {
  const max = Math.max(1, ...items.map((i) => Math.abs(i.value)));
  if (!items.length) return <div className="text-xs text-muted-foreground">Нет данных</div>;
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={`${item.name}-${idx}`} className="text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate" title={item.name}>{item.name}</span>
            <span className={cn("shrink-0 font-medium tabular-nums", VIZ_TONE[item.tone].text)}>
              {item.signed ? signed(item.value) : int0(item.value)}
              {item.suffix ?? ""}
            </span>
          </div>
          <div className="mt-1 h-1.5 rounded bg-muted">
            <div
              className={cn("h-full rounded", VIZ_TONE[item.tone].bar)}
              style={{ width: `${Math.max(3, Math.round((Math.abs(item.value) / max) * 100))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: (string | number)[][];
}): React.ReactElement {
  if (!rows.length) return <div className="text-xs text-muted-foreground">Нет данных</div>;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b text-muted-foreground">
          {columns.map((c, i) => (
            <th key={c} className={cn("px-1.5 py-1 font-medium", i === 0 ? "text-left" : "text-right")}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} className="border-b last:border-0">
            {r.map((v, i) => (
              <td key={i} className={cn("px-1.5 py-1 tabular-nums", i === 0 ? "text-left" : "text-right", i === 0 && "max-w-40 truncate")}>
                {typeof v === "number" ? int0(v) : v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Kpi({ label, value, note }: { label: string; value: string; note: string }): React.ReactElement {
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] text-muted-foreground">{note}</div>
    </div>
  );
}

export function AnalyticsTab({ report }: { report: OrdersMatrixReport }): React.ReactElement {
  const { rows, ctx } = useMemo(() => buildView(report, "all", ""), [report]);

  const d = useMemo(() => computeAnalytics(rows, ctx), [rows, ctx]);

  const attrsQ = useQuery({ queryKey: ["orders", "refs", "attrs"], queryFn: planningApi.listAttrs });
  const attrsData = useMemo(() => {
    const attrs = new Map((attrsQ.data ?? []).map((a) => [a.article, a]));
    const byGroup = new Map<string, { qty: number; ozon: number; wb: number; ym: number }>();
    const byChar = new Map<string, number>();
    for (const row of rows) {
      const a = attrs.get(row.article);
      const group = a?.product_type || "Не заполнено";
      const g = byGroup.get(group) ?? { qty: 0, ozon: 0, wb: 0, ym: 0 };
      g.qty += row.qtySum;
      g.ozon += row.mps.ozon;
      g.wb += row.mps.wb;
      g.ym += row.mps.ym;
      byGroup.set(group, g);
      const char = a?.characteristic || "Не заполнено";
      byChar.set(char, (byChar.get(char) ?? 0) + row.qtySum);
    }
    const groups = [...byGroup.entries()]
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 10);
    const chars = [...byChar.entries()]
      .map(([name, value]) => ({ name, value, tone: "amber" as const }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    const hasAttrs = (attrsQ.data ?? []).some((a) => a.product_type || a.characteristic);
    return { groups, chars, hasAttrs };
  }, [attrsQ.data, rows]);

  return (
    <div className="space-y-3">
      {/* Сводка (порт analytics-summary + деньги v1; план-факт отложен вместе с планами) */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-7">
        <Kpi label="Заказы" value={int0(d.totalQty)} note="за выбранный период" />
        <Kpi label="Выручка" value={`${int0(d.totalRevenue)} ₽`} note="по данным заказов" />
        <Kpi label="Себестоимость" value={`${int0(d.totalCost)} ₽`} note="где известна" />
        <Kpi label="Маржа" value={`${int0(d.totalMargin)} ₽`} note="реальная, из финотчётов" />
        <Kpi label="Маржинальность" value={`${String(d.marginPctVal).replace(".", ",")}%`} note="маржа / выручка" />
        <Kpi label="Артикулы" value={int0(rows.length)} note="после фильтров" />
        <Kpi label="Активные 30д" value={int0(d.active30)} note="есть продажи за 30 дней" />
      </div>

      <div className="grid gap-3 xl:grid-cols-2">
        <Card title="Динамика заказов по месяцам" sub={`${ctx.months.length} мес. · видно сезонность и пики`}>
          <MonthBar months={ctx.months} values={d.qtyByMonth} tone="qty" unit="шт" />
        </Card>
        <Card title="Выручка по месяцам" sub="денежный вклад, а не только штуки">
          <MonthBar months={ctx.months} values={d.revenueByMonth} tone="revenue" unit="₽" />
        </Card>
      </div>

      <div className="grid gap-3 xl:grid-cols-2">
        <Card title="Маржа по месяцам" sub="реальная маржа: WB — финотчёт, Ozon — начисления минус себестоимость">
          <MonthBar months={ctx.months} values={d.marginByMonth} tone="margin" unit="₽" />
        </Card>
        <div className="grid gap-3 sm:grid-cols-2">
          <MpDonut rows={rows} />
          <Card title="ABC/XYZ" sub="тепловая карта">
            <div className="grid grid-cols-3 gap-1.5">
              {d.abcItems.map((item) => (
                <div
                  key={item.name}
                  className="rounded-md border px-2 py-2 text-center"
                  style={{ backgroundColor: `rgba(2,132,199,${(0.06 + (item.value / d.abcMax) * 0.3).toFixed(2)})` }}
                >
                  <div className="text-[11px] text-muted-foreground">{item.name}</div>
                  <div className="text-sm font-semibold tabular-nums">{int0(item.value)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <Card title="Концентрация продаж" sub="топ-20 и накопленная доля">
          <VizList items={d.paretoItems} />
        </Card>
        <Card title="Топ роста" sub="MTD к прошлому месяцу">
          <VizList items={d.growthItems} />
        </Card>
        <Card title="Топ просадки" sub="MTD к прошлому месяцу">
          <VizList items={d.dropItems} />
        </Card>
        <Card title="Риск остатков" sub="только товары с рассчитанным запасом">
          {d.riskItems.length ? (
            <VizList items={d.riskItems} />
          ) : (
            <div className="text-xs text-muted-foreground">
              Нет товаров, где можно рассчитать запас в днях: нужны продажи за 30 дней и положительный остаток.
            </div>
          )}
        </Card>
        <Card title="Остатки" sub="топ совокупного остатка">
          <VizList items={d.stockItems} />
        </Card>
        <Card title="Продажи без остатка" sub="критично: продажи есть, остаток ноль">
          <MiniTable columns={["Артикул", "30 дней", "Остаток"]} rows={d.salesNoStockRows} />
        </Card>
        <Card title="Нет продаж при остатке" sub="что требует проверки">
          <MiniTable columns={["Артикул", "Остаток", "30 дней"]} rows={d.noSalesStockRows} />
        </Card>
        <Card title="Цена, выручка и маржа" sub="топ артикулов по заказам">
          <MiniTable
            columns={["Артикул", "Заказы", "Ср. цена", "Выручка", "Маржа"]}
            rows={d.priceRows}
          />
        </Card>
        <Card title="Средняя цена" sub="сравнение по топ-артикулам">
          <MonthlessBar items={d.priceItems} />
        </Card>
        <Card title="Группы" sub="топ по заказам (тип продукта из справочника)">
          {attrsData.hasAttrs ? (
            <GroupBubbles groups={attrsData.groups} />
          ) : (
            <div className="text-xs text-muted-foreground">
              Заполните типы продукта в «Настройки → Справочник артикулов».
            </div>
          )}
        </Card>
        <Card title="Маркетплейсы внутри групп" sub="где какая площадка тянет продажи">
          {attrsData.hasAttrs ? (
            <GroupStacks groups={attrsData.groups} />
          ) : (
            <div className="text-xs text-muted-foreground">Нет данных справочника.</div>
          )}
        </Card>
        <Card title="Характеристики" sub="структура заказов">
          <VizList items={attrsData.chars} />
        </Card>
      </div>
    </div>
  );
}

interface GroupAgg {
  name: string;
  qty: number;
  ozon: number;
  wb: number;
  ym: number;
}

/** «Пузыри» групп (порт viz-bubbles, стр. 4728-4731). */
function GroupBubbles({ groups }: { groups: GroupAgg[] }): React.ReactElement {
  const max = Math.max(1, ...groups.map((g) => g.qty));
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 py-2">
      {groups.slice(0, 8).map((g) => {
        const size = Math.max(56, Math.round(56 + (g.qty / max) * 70));
        return (
          <div
            key={g.name}
            className="flex flex-col items-center justify-center rounded-full bg-sky-100 text-center dark:bg-sky-950"
            style={{ width: size, height: size }}
            title={`${g.name}: ${int0(g.qty)}`}
          >
            <span className="text-xs font-semibold tabular-nums">{int0(g.qty)}</span>
            <span className="max-w-[90%] truncate px-1 text-[9px] text-muted-foreground">{g.name}</span>
          </div>
        );
      })}
    </div>
  );
}

const STACK_COLORS = { ozon: "#55b9dd", wb: "#7467d8", ym: "#58a876" };

/** Стек МП внутри групп (порт stackedGroupViz, стр. 4651-4660). */
function GroupStacks({ groups }: { groups: GroupAgg[] }): React.ReactElement {
  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const total = Math.max(1, g.ozon + g.wb + g.ym);
        return (
          <div key={g.name} className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate">{g.name}</span>
              <span className="font-medium tabular-nums">{int0(g.qty)}</span>
            </div>
            <div
              className="mt-1 flex h-2 overflow-hidden rounded"
              title={`Ozon: ${int0(g.ozon)} · WB: ${int0(g.wb)} · ЯМ: ${int0(g.ym)}`}
            >
              <span style={{ width: `${(g.ozon / total) * 100}%`, backgroundColor: STACK_COLORS.ozon }} />
              <span style={{ width: `${(g.wb / total) * 100}%`, backgroundColor: STACK_COLORS.wb }} />
              <span style={{ width: `${(g.ym / total) * 100}%`, backgroundColor: STACK_COLORS.ym }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Бар по артикулам (порт largeBarViz для средней цены). */
function MonthlessBar({
  items,
}: {
  items: { label: string; value: number }[];
}): React.ReactElement {
  const isDark = useIsDark();
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={items} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} strokeOpacity={0.25} />
        <XAxis dataKey="label" tick={axisTick(isDark, 9)} tickLine={false} axisLine={false} interval={0} angle={-30} textAnchor="end" height={52} />
        <YAxis tick={axisTick(isDark)} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => int0(v)} />
        <Tooltip
          cursor={{ fillOpacity: 0.06 }}
          content={({ active, payload }) => {
            const p = active && payload?.[0]?.payload;
            if (!p) return null;
            return (
              <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md">
                <div className="font-medium">{p.label}</div>
                <div className="text-base font-semibold tabular-nums">{int0(p.value)} ₽ ср.</div>
              </div>
            );
          }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={30} fill="#8b7ee3" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function computeAnalytics(rows: ViewRow[], ctx: ViewContext) {
  const months = ctx.months;
  const qtyByMonth = months.map((_, i) => rows.reduce((s, r) => s + (r.qty[i] ?? 0), 0));
  const revenueByMonth = months.map((_, i) => rows.reduce((s, r) => s + (r.total[i] ?? 0), 0));
  const marginByMonth = months.map((_, i) => rows.reduce((s, r) => s + (r.margin[i] ?? 0), 0));
  const totalQty = rows.reduce((s, r) => s + r.qtySum, 0);
  const totalRevenue = rows.reduce((s, r) => s + r.totalSum, 0);
  const totalCost = rows.reduce((s, r) => s + r.costSum, 0);
  const totalMargin = rows.reduce((s, r) => s + r.marginSum, 0);

  const enriched: { row: ViewRow; m: RowMetrics }[] = rows.map((row) => ({
    row,
    m: rowMetrics(row, ctx),
  }));
  const active30 = enriched.filter((x) => x.m.last30 > 0).length;

  const abcItems = (() => {
    const map = new Map<string, number>();
    for (const r of rows) map.set(r.abcxyz, (map.get(r.abcxyz) ?? 0) + r.qtySum);
    return [...map.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 9);
  })();
  const abcMax = Math.max(1, ...abcItems.map((i) => i.value));

  // Парето топ-20 с накопленной долей (порт стр. 4713-4718)
  let acc = 0;
  const paretoItems = [...rows]
    .sort((a, b) => b.qtySum - a.qtySum)
    .slice(0, 20)
    .map((row) => {
      acc += row.qtySum;
      const pct = totalQty ? ((acc / totalQty) * 100).toFixed(1).replace(".", ",") : "0";
      return {
        name: row.article,
        value: row.qtySum,
        suffix: ` · ${pct}%`,
        tone: acc / Math.max(1, totalQty) <= 0.8 ? ("sky" as const) : ("amber" as const),
      };
    });

  const growthItems = [...enriched]
    .sort((a, b) => b.m.deltaMtd - a.m.deltaMtd)
    .slice(0, 10)
    .map((x) => ({
      name: `${x.row.article} · ${x.row.name}`,
      value: x.m.deltaMtd,
      tone: x.m.deltaMtd >= 0 ? ("green" as const) : ("red" as const),
      signed: true,
    }));
  const dropItems = [...enriched]
    .sort((a, b) => a.m.deltaMtd - b.m.deltaMtd)
    .slice(0, 8)
    .map((x) => ({
      name: `${x.row.article} · ${x.row.name}`,
      value: x.m.deltaMtd,
      tone: "red" as const,
      signed: true,
    }));
  const riskItems = enriched
    .filter((x) => x.m.last30 > 0 && x.row.stockTotal > 0 && (x.m.stockDays ?? 0) > 0)
    .sort((a, b) => (a.m.stockDays ?? 0) - (b.m.stockDays ?? 0))
    .slice(0, 12)
    .map((x) => ({
      name: `${x.row.article} · ${x.row.name}`,
      value: x.m.stockDays ?? 0,
      suffix: " дн",
      tone: (x.m.stockDays ?? 0) <= 21 ? ("red" as const) : ("amber" as const),
    }));
  const stockItems = [...enriched]
    .sort((a, b) => b.row.stockTotal - a.row.stockTotal)
    .slice(0, 10)
    .map((x) => ({
      name: `${x.row.article} · ${x.row.name}`,
      value: x.row.stockTotal,
      tone: x.row.stockTotal > 0 && x.m.last30 === 0 ? ("amber" as const) : ("sky" as const),
    }));

  const salesNoStockRows = enriched
    .filter((x) => x.m.last30 > 0 && x.row.stockTotal <= 0)
    .sort((a, b) => b.m.last30 - a.m.last30)
    .slice(0, 10)
    .map((x) => [x.row.article, x.m.last30, 0] as (string | number)[]);
  const noSalesStockRows = enriched
    .filter((x) => x.row.stockTotal > 0 && x.m.last30 === 0)
    .sort((a, b) => b.row.stockTotal - a.row.stockTotal)
    .slice(0, 10)
    .map((x) => [x.row.article, x.row.stockTotal, x.m.last30] as (string | number)[]);

  const priceTop = rows
    .filter((r) => r.qtySum > 0)
    .sort((a, b) => b.qtySum - a.qtySum)
    .slice(0, 12);
  const priceRows = priceTop.map((r) => [
    r.article,
    r.qtySum,
    `${int0(r.qtySum ? Math.round(r.totalSum / r.qtySum) : 0)} ₽`,
    `${int0(Math.round(r.totalSum))} ₽`,
    `${int0(Math.round(r.marginSum))} ₽`,
  ] as (string | number)[]);
  const priceItems = priceTop.map((r) => ({
    label: r.article,
    value: r.qtySum ? Math.round(r.totalSum / r.qtySum) : 0,
  }));

  return {
    qtyByMonth,
    revenueByMonth,
    marginByMonth,
    totalQty,
    totalRevenue,
    totalCost,
    totalMargin,
    marginPctVal: marginPct(totalMargin, totalRevenue),
    active30,
    abcItems,
    abcMax,
    paretoItems,
    growthItems,
    dropItems,
    riskItems,
    stockItems,
    salesNoStockRows,
    noSalesStockRows,
    priceRows,
    priceItems,
  };
}
